import Head from "next/head";
import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import {
  Box,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from "@mui/material";
import { AiOutlineDollarCircle } from "react-icons/ai";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { TbShirtSport } from "react-icons/tb";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";

import { constants } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const purchaseSeatsLanguages = {
  similarSeatModalPrimaryButton: "Apply selection for all games",
  similarSeatModalSecondaryButton: "I want to choose seats",
  progressBarSuccessText:
    "You're good to go! Add more games or select Continue to view seating options.",
  progressBarAditionalText: " to continue!",
  similarSeatModalBody: (seatTitle: string) => {
    return (
      <>
        Do you want to apply <b>{seatTitle.replace(/<br \/>/g, "")}</b> for all
        games in your pack?
      </>
    );
  },
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Connect to Account Manager",
    description:
      "Connect your Las Vegas Ballpark Account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Tickets will be available in your{" "}
        <Link
          href={"https://am.ticketmaster.com/aviatorslv/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Las Vegas Aviators
        </Link>{" "}
        account. Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the Aviators Suite Rentals work?",
    description:
      "The Aviators Suite Rentals allows fans to select any seat to any game. Simply log into your Las Vegas Ballpark Account manager and select games.",
  },
  {
    title: "Can I choose suites throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are event tickets delivered?",
    description:
      "Get your tickets - Ticket will be available in the new Las Vegas Ballpark app or in your Account Manager.",
  },
  {
    title: "When will I receive my ticket?",
    description:
      "If your reservation is accepted, a Las Vegas Aviators representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "Is re-entry allowed?",
    description:
      "Re-entry is allowed only at specific gate locations. Before exiting the stadium, please confirm which gates allow re-entry with a Las Vegas Aviators guest services representative.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a Las Vegas Aviators representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export function CheckoutTerms() {
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        // fontWeight={"bold"}
        // textDecor="underline"

        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link
        // fontWeight={"bold"}
        // textDecor="underline"
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Privacy Policy
      </Link>
      .
    </>
  );
}

export function createPerkTotalLabel(cartManager: CartManagerState) {
  const cart = cartManager?.cart;
  const parkingPerks = cart
    ?.map((evt) => {
      return evt?.perks?.filter((perk) => {
        return perk?.perkCode === "parking";
      });
    })
    .flat();
  const otherPerks = cart
    ?.map((evt) => {
      return evt?.perks?.filter((perk) => {
        return perk?.perkCode !== "parking";
      });
    })
    .flat();
  if (parkingPerks.length > 0 && otherPerks.length > 0) {
    return "Parking + Add On Total";
  } else if (parkingPerks.length > 0) {
    return "Parking Total";
  } else {
    return "Add On Total";
  }
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '3360222884215614');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=3360222884215614&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
